/**
 * Grade tier definitions for Nudge.
 * Maps a numeric grade level (0–12, where 0 = Kindergarten) to a 4-band tier
 * used throughout prompt builders, ThemeContext, and the fallback question bank.
 */

export type GradeTier = 'K-2' | '3-5' | '6-8' | '9-12'

export function getGradeTier(gradeLevel: number): GradeTier {
  if (gradeLevel <= 2) return 'K-2'
  if (gradeLevel <= 5) return '3-5'
  if (gradeLevel <= 8) return '6-8'
  return '9-12'
}

export function formatGrade(grade: number): string {
  if (grade === 0) return 'K'
  if (grade === 1) return '1st'
  if (grade === 2) return '2nd'
  if (grade === 3) return '3rd'
  return `${grade}th`
}

/**
 * Grade groupings used by the Onboarding picker (and any future grade selector,
 * e.g. Parent Dashboard). Covers every value 0..12 exactly once.
 */
export const GRADE_GROUPS: ReadonlyArray<{ label: string; grades: readonly number[] }> = [
  { label: 'Elementary', grades: [0, 1, 2, 3, 4, 5] },
  { label: 'Middle',     grades: [6, 7, 8] },
  { label: 'High',       grades: [9, 10, 11, 12] },
] as const

export const GRADE_TIER_CONFIG: Record<GradeTier, {
  label:          string
  vocabularyNote: string
  sentenceLen:    string
  conceptDepth:   string
  exampleStyle:   string
}> = {
  'K-2': {
    label:          'Kindergarten to 2nd grade',
    vocabularyNote: 'Use only simple, everyday words a 6-8 year old knows. Short sentences (max 10 words). No multi-syllable academic terms.',
    sentenceLen:    'very short (5-8 words)',
    conceptDepth:   'concrete and visual — use objects, colors, and counting',
    exampleStyle:   'everyday objects like apples, dogs, crayons, and toys',
  },
  '3-5': {
    label:          '3rd to 5th grade',
    vocabularyNote: 'Use clear, simple language for 8-11 year olds. Introduce academic vocabulary with brief context clues.',
    sentenceLen:    'short to medium (8-15 words)',
    conceptDepth:   'concrete with some abstract — introduce patterns and rules',
    exampleStyle:   'relatable scenarios: school, sports, animals, food',
  },
  '6-8': {
    label:          '6th to 8th grade',
    vocabularyNote: 'Use grade-level academic vocabulary for 11-14 year olds. Can use subject-specific terms.',
    sentenceLen:    'medium (10-20 words)',
    conceptDepth:   'abstract reasoning — patterns, cause/effect, analysis',
    exampleStyle:   'real-world applications and age-appropriate scenarios',
  },
  '9-12': {
    label:          'High school (9th-12th grade)',
    vocabularyNote: 'Use standard academic and subject-specific vocabulary for 14-18 year olds.',
    sentenceLen:    'standard academic prose',
    conceptDepth:   'analytical and evaluative — synthesis across concepts',
    exampleStyle:   'complex real-world problems, historical events, scientific phenomena',
  },
}
